import { FC, useState, useEffect, useCallback } from "react";
import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CustomTable from "../../components/common/CustomTable/CustomTable";
import { ModalConfirm } from "../../components/common/ModalConfirm/ModalConfirm";
import { useNavigate } from "react-router-dom";
import { RoutesMap } from "../../types";
import { ApiStatus } from "../../types/api/status";
import { CustomBackdrop } from "../../components/common/CustomBackdrop/CustomBackdrop";
import { useCycleStore } from "../../hooks/useCycleStore";
import { Cycle } from "../../types/slices/cycleType";
import { AddStudentCycleModal } from "./components/AddStudentModal";
import { Student } from "../../types/slices/studentType";
import { UploadExcelModal } from "./components/UploadExcelModal";
import { useStudentStore } from "../../hooks/useStudentStore";
import { CustomSnackbar } from "../../components/common/CustomSnackbar/CustomSnackbar";
import {
  WatanaBatchSummary,
  WatanaBatchesDrawer,
} from "./components/WatanaBatchesDrawer";
import { BackButton } from "../../components/common/BackButton/BackButton";
import { getWatanaBatchStatus } from "./components/watanaBatchStatus";

const columns = [
  { type: "options", field: "options", label: "Opciones" },
  { type: "text", field: "dni_student", label: "DNI" },
  {
    type: "text",
    field: "name_student",
    label: "NOMBRE",
    format: (row) => row.name_student + " " + row.surname_student,
  },
  { type: "input", field: "nota", label: "NOTA" },
  { type: "checkbox", field: "status_certificate", label: "DIGITAL" },
  {
    type: "text",
    field: "digital_status",
    label: "Estado",
    format: (row) =>
      row.digital_status === "0"
        ? "No"
        : row.digital_status === "1"
        ? "Pendiente"
        : "Firmado",
  },
  { type: "checkbox", field: "print_certificate", label: "FISICO" },
  {
    type: "text",
    field: "sent_print",
    label: "ENVIADO",
    format: (row) =>
      row.print_certificate === "1"
        ? row.sent_print === "1"
          ? "Si"
          : "No"
        : "",
  },
  { type: "multi-select", field: "", label: "" },
];

const StudentCycle: FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [rowSelected, setRowSelected] = useState<Student>({} as Student);
  const [modalDeleteConfirm, setModalDeleteConfirm] = useState<boolean>(false);
  const [openModalExcel, setOpenModalExcel] = useState<boolean>(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [studentsByCycleData, setStudentsByCycleData] = useState<Student[]>([]);
  const [filterBy, setFilterBy] = useState<string>("all");
  const [lastBatchCode, setLastBatchCode] = useState<string>("");
  const [lastBatchStatus, setLastBatchStatus] = useState<string>("");
  const [lastBatchStatusCode, setLastBatchStatusCode] = useState<string>("");
  const [openBatchesDrawer, setOpenBatchesDrawer] = useState<boolean>(false);
  const {
    status,
    statusStudents,
    studentByCycle,
    selectedCycle,
    openModal,
    getStudentByCycle,
    getStudentByCycleSearch,
    getTemplateStudentByCycle,
    setOpenModal,
    changeIsEdit,
    setSelectedCycle,
    deleteCycleStudent,
    fetchStudentByCycle,
    editStudentCycleStore,
    createWatanaSignatureBatch,
    getWatanaSignatureBatches,
    cancelWatanaSignatureBatch,
  } = useCycleStore();

  const { getCertificatePDFMultipleTemplate } = useStudentStore();
  const isWatanaFlow = [
    "watana-focus",
    "watana-corlad",
    "watana-corlad-provincias",
  ].includes(
    selectedCycle?.entity_code || ""
  );
  const latestBatchStatus = getWatanaBatchStatus(
    lastBatchStatusCode,
    lastBatchStatus
  );

  useEffect(() => {
    if (selectedCycle.id) {
      setLoading(true);
      getStudentByCycle(selectedCycle.id).then((data) => {
        setStudentsByCycleData(data);
        setLoading(false);
      });
    } else {
      navigate(RoutesMap.CYCLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycle, setLoading]);

  const refreshLatestBatchSummary = useCallback(async () => {
    if (!isWatanaFlow || !selectedCycle.id) return;
    const batches = await getWatanaSignatureBatches(selectedCycle.id);
    if (!Array.isArray(batches)) {
      return;
    }
    if (batches.length === 0) {
      setLastBatchCode("");
      setLastBatchStatus("");
      setLastBatchStatusCode("");
      return;
    }
    const latest = batches[0] as WatanaBatchSummary;
    setLastBatchCode(latest.batch_code || "");
    setLastBatchStatus(
      getWatanaBatchStatus(latest.status, latest.status_label).label
    );
    setLastBatchStatusCode(String(latest.status || ""));
  }, [getWatanaSignatureBatches, isWatanaFlow, selectedCycle.id]);

  useEffect(() => {
    refreshLatestBatchSummary();
  }, [refreshLatestBatchSummary]);

  const onAdd = () => {
    changeIsEdit(false);
    setOpenModal(true);
  };

  const onDelete = (rowSelected) => {
    setRowSelected(rowSelected);
    setModalDeleteConfirm(true);
  };

  const onDeleteConfirm = async () => {
    let idsStudent = JSON.stringify([rowSelected?.id_student]);

    const response = await deleteCycleStudent(
      selectedCycle?.id || 0,
      idsStudent
    );
    if (response === true) {
      // setSelected({} as Module);
      setModalDeleteConfirm(false);
      getStudentByCycle(selectedCycle.id || 0).then((data) =>
        setStudentsByCycleData(data)
      );
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const goBack = () => {
    setSelectedCycle({} as Cycle);
    navigate(RoutesMap.CYCLE);
  };

  const onChange = (value, updatedRow, field) => {
    const rowIndex = studentsByCycleData.findIndex(
      (row) => row.id === updatedRow.id
    );

    if (rowIndex !== -1) {
      const updatedStudentByCycle: any = [...studentsByCycleData];

      updatedStudentByCycle[rowIndex] = {
        ...updatedStudentByCycle[rowIndex],
        [field]: value,
      };

      setStudentsByCycleData(updatedStudentByCycle);
      fetchStudentByCycle(updatedStudentByCycle);
    }
  };

  const onClear = () => {
    if (!isSearch) {
      getStudentByCycle(selectedCycle.id || 0).then((data) =>
        setStudentsByCycleData(data)
      );
    }
  };

  const onSave = async () => {
    const rowFind = studentsByCycleData.find(
      (item) => item.id === rowSelected.id
    );
    await editStudentCycleStore(rowFind?.id || 0, {
      nota: rowFind?.nota,
      status_certificate: rowFind?.status_certificate,
      print_certificate: rowFind?.print_certificate,
    });
    onClear();
    setRowSelected({} as Student);
  };

  const onDownloadExcel = async () => {
    const response: any = await getTemplateStudentByCycle(
      selectedCycle.id || 0
    );
    if (response) {
      const btn_excel = document.createElement("a");
      btn_excel.href = `${process.env.REACT_APP_ROOT_URL}/${response}`;
      document.body.appendChild(btn_excel);
      btn_excel.click();
      document.body.removeChild(btn_excel);
    }
  };

  const onDownloadTemplate = useCallback(() => {
    window.open(
      `/templates/${selectedCycle.entity_code}-certificate-tmpl.pdf`,
      "_blank"
    );
  }, [selectedCycle]);

  const onUploadExcel = async () => {
    setOpenModalExcel(true);
  };

  const onSearch = (value) => {
    if (value.length >= 3) {
      setIsSearch(true);
      getStudentByCycleSearch(selectedCycle?.id || 0, value).then((data) =>
        setStudentsByCycleData(data)
      );
    }
    if (value.length === 0) {
      setIsSearch(false);
      getStudentByCycle(selectedCycle?.id || 0).then((data) =>
        setStudentsByCycleData(data)
      );
    }
  };

  const onPrintMultipleCertificate = async (side: string, type: string) => {
    try {
      if (!selectedRows.length) {
        return CustomSnackbar("error", "Elija al menos un certificado");
      }
      const response = await getCertificatePDFMultipleTemplate(
        selectedCycle?.id,
        selectedRows.map(({ id_certificate }) => id_certificate),
        type,
        selectedCycle.entity_code,
        side
      );

      const url = window.URL.createObjectURL(response);
      const win = window.open(url, "_blank");
      win?.focus();
      URL.revokeObjectURL(url);
      getStudentByCycle(selectedCycle.id || 0).then((data) =>
        setStudentsByCycleData(data)
      );
    } catch (e) {
      console.warn("No se pudo descargar. Volver a intentar.");
    }
  };

  const onPrintCertificate = async (rowSelected) => {
    try {
      const response = await getCertificatePDFMultipleTemplate(
        selectedCycle?.id,
        [rowSelected?.id_certificate],
        "format",
        selectedCycle.entity_code
      );
      const url = window.URL.createObjectURL(response);
      const win = window.open(url, "_blank");
      win?.focus();
      URL.revokeObjectURL(url);
      onClear();
    } catch (e) {
      console.warn("No se pudo descargar. Volver a intentar.");
    }
  };

  const onGenerateSignatureBatch = async () => {
    if (!isWatanaFlow) return;

    const idsForBatch = studentsByCycleData
      .filter((row) => !!row.id_certificate && row.status_certificate === "1")
      .map((row) => row.id_certificate as number);

    if (!idsForBatch.length) {
      return CustomSnackbar(
        "error",
        "No hay certificados habilitados para generar lote"
      );
    }

    const result = await createWatanaSignatureBatch(selectedCycle?.id || 0, idsForBatch);
    if (result?.batch_code) {
      setLastBatchCode(result.batch_code);
      setLastBatchStatus(getWatanaBatchStatus(result.status).label);
      setLastBatchStatusCode(String(result.status || ""));
      refreshLatestBatchSummary();
    }
  };

  const onRefreshBatch = async () => {
    await refreshLatestBatchSummary();
    if (lastBatchCode) {
      CustomSnackbar("success", `Lote ${lastBatchCode} actualizado`);
    }
  };

  const onBatchCancelled = useCallback(async () => {
    await refreshLatestBatchSummary();
    if (selectedCycle.id) {
      const data = await getStudentByCycle(selectedCycle.id);
      if (Array.isArray(data)) {
        setStudentsByCycleData(data);
      }
    }
  }, [getStudentByCycle, refreshLatestBatchSummary, selectedCycle.id]);

  const onPrintCertificateChange = useCallback(
    (value, row) => {
      if (value) {
        setSelectedRows((_: any) => [..._, row]);
      } else {
        setSelectedRows((rows) => {
          const rowIndex = rows.findIndex(
            (_: any) => _.id_certificate === row.id_certificate
          );
          if (rowIndex >= 0) {
            rows.splice(rowIndex, 1);
          }
          return [...rows];
        });
      }
    },
    [setSelectedRows]
  );

  const filterBySentPrint = useCallback(
    (value) => {
      const filtered = studentByCycle.filter(
        ({ sent_print, print_certificate }) =>
          value === "all" ||
          (value === "print" && print_certificate === "1") ||
          (sent_print === value && print_certificate === "1")
      );
      setFilterBy(value);
      setStudentsByCycleData(filtered);
      setPage(0);
    },
    [studentByCycle, setFilterBy, setStudentsByCycleData, setPage]
  );

  const onCheckAllToPrint = useCallback(
    (value) => {
      if (value) {
        const selectedToPrint = studentsByCycleData.filter(
          ({ print_certificate }) => print_certificate === "1"
        );
        setSelectedRows(selectedToPrint);
      } else {
        setSelectedRows([]);
      }
    },
    [studentsByCycleData, setSelectedRows]
  );

  const getStudentByCycleCallback = useCallback(
    (value) => {
      getStudentByCycle(value).then((data) => setStudentsByCycleData(data));
    },
    [getStudentByCycle, setStudentsByCycleData]
  );

  return (
    <>
      {statusStudents === ApiStatus.FETCHING && <CustomBackdrop open={true} />}
      {status === ApiStatus.FETCHING && <CustomBackdrop open={true} />}

      <BackButton onClick={goBack} />
      <Grid item xs={12}>
        {isWatanaFlow && (
          <Box
            sx={{
              mb: 2,
              mt: 0.5,
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "rgba(1, 77, 223, 0.035)",
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: 1,
                rowGap: 0.75,
              }}
            >
              {lastBatchCode ? (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    Último lote:{" "}
                    <Box component="span" color="primary.dark" fontWeight={600}>
                      {lastBatchCode}
                    </Box>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Estado:
                  </Typography>
                  <Chip
                    size="small"
                    label={latestBatchStatus.label}
                    color={latestBatchStatus.color}
                    variant={latestBatchStatus.variant}
                  />
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<RefreshRoundedIcon />}
                    onClick={onRefreshBatch}
                    sx={{ minHeight: 32, px: 1 }}
                  >
                    Actualizar
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Sin lotes registrados para este ciclo
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenBatchesDrawer(true)}
              sx={{ flexShrink: 0 }}
            >
              Ver lotes
            </Button>
          </Box>
        )}
        <CustomTable
          isCertificate
          isWatanaFlow={isWatanaFlow}
          isMultipleEnabled={!Boolean(selectedRows.length)}
          title={
            selectedCycle
              ? `Estudiantes de ${selectedCycle?.name} - ${selectedCycle.courses?.name} - ${selectedCycle.entity_code}`
              : "Estudiantes del Ciclo"
          }
          columns={columns}
          loading={loading}
          rows={studentsByCycleData}
          onRowClick={() => {}}
          hasOptions
          onDelete={onDelete}
          onAddFn={onAdd}
          onSearch={onSearch}
          onDownloadExcel={onDownloadExcel}
          onUploadExcel={onUploadExcel}
          onChangeInput={onChange}
          filterBy={filterBy}
          filterBySentPrint={filterBySentPrint}
          onPrintCertificate={onPrintCertificate}
          onPrintCertificateChange={onPrintCertificateChange}
          onPrintMultipleCertificate={onPrintMultipleCertificate}
          onDownloadTemplate={onDownloadTemplate}
          onGenerateSignatureBatch={onGenerateSignatureBatch}
          onSwitch
          editable
          onCheckAllToPrint={onCheckAllToPrint}
          selectedRows={selectedRows}
          rowSelected={rowSelected}
          setRowSelected={setRowSelected}
          setSelectedRows={setSelectedRows}
          onClear={onClear}
          onSave={onSave}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Grid>
      {modalDeleteConfirm && (
        <ModalConfirm
          open={modalDeleteConfirm}
          closeModal={() => {
            setModalDeleteConfirm(false);
          }}
          onCancel={() => {
            setModalDeleteConfirm(false);
          }}
          onConfirm={onDeleteConfirm}
          status={statusStudents}
        />
      )}
      {openModalExcel && (
        <UploadExcelModal
          openModal={openModalExcel}
          setOpenModal={setOpenModalExcel}
          selectedCycle={selectedCycle}
          getStudentByCycle={getStudentByCycleCallback}
        />
      )}
      {openModal && (
        <AddStudentCycleModal onAddStudent={setStudentsByCycleData} />
      )}
      {isWatanaFlow && (
        <WatanaBatchesDrawer
          open={openBatchesDrawer}
          onClose={() => setOpenBatchesDrawer(false)}
          idcycle={selectedCycle.id || 0}
          fetchBatches={getWatanaSignatureBatches}
          cancelBatch={cancelWatanaSignatureBatch}
          onBatchCancelled={onBatchCancelled}
          loading={status === ApiStatus.FETCHING}
        />
      )}
    </>
  );
};

export default StudentCycle;
